import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import {
  GearSix, CreditCard, Bank, Wallet, Bell, Spinner, FloppyDisk, Eye, CurrencyCircleDollar, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_MODES = [
  {
    value: "manual",
    label: "Sadece Manuel",
    desc: "Nakit/havale alirsiniz, bakiyeyi kendiniz yuklersiniz",
    tenantOnline: false,
    icon: Wallet,
    color: "border-slate-300 bg-slate-50",
    activeColor: "border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200",
  },
  {
    value: "customer_pos",
    label: "Kendi POS + Manuel",
    desc: "Kiraci online oder (sizin POS hesabiniz), siz de manuel yukleyebilirsiniz",
    tenantOnline: true,
    icon: CreditCard,
    color: "border-slate-300 bg-slate-50",
    activeColor: "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200",
  },
  {
    value: "platform_pos",
    label: "Platform POS + Manuel",
    desc: "Kiraci online oder (Elworks POS), komisyonlu. Siz de manuel yukleyebilirsiniz",
    tenantOnline: true,
    icon: Bank,
    color: "border-slate-300 bg-slate-50",
    activeColor: "border-amber-600 bg-amber-50 ring-2 ring-amber-200",
  },
];

const CustomerSettingsPage = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/settings`);
      setSettings(res.data);
    } catch { toast.error("Ayarlar yuklenirken hata olustu"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (settings.topup_min_amount >= settings.topup_max_amount) {
        toast.error("Minimum tutar, maksimum tutardan kucuk olmali");
        setSaving(false);
        return;
      }
      await axios.put(`${API_URL}/api/customer/settings`, settings);
      toast.success("Ayarlar kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydetme hatasi");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  const activeMode = PAYMENT_MODES.find(m => m.value === settings?.payment_mode) || PAYMENT_MODES[0];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("settings.title")}</h1>
          <p className="text-slate-500 mt-1">Odeme modu, limitler ve bildirim ayarlari</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-settings-btn">
          {saving ? <Spinner size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
          {saving ? "Kaydediliyor..." : t("common.save")}
        </Button>
      </div>

      {/* Payment Mode Selection */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <CreditCard size={20} className="text-indigo-600" />
            Odeme Modu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PAYMENT_MODES.map((mode) => {
              const Icon = mode.icon;
              const isActive = settings?.payment_mode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, payment_mode: mode.value })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${isActive ? mode.activeColor : mode.color} hover:shadow-sm`}
                  data-testid={`payment-mode-${mode.value}`}
                >
                  <Icon size={28} className={isActive ? "text-indigo-600" : "text-slate-400"} />
                  <p className="font-semibold text-slate-900 mt-2">{mode.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{mode.desc}</p>
                  <div className="mt-3">
                    <Badge className={mode.tenantOnline ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                      {mode.tenantOnline ? "Kiraci Online Odeme: Acik" : "Kiraci Online Odeme: Kapali"}
                    </Badge>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Summary table */}
          <div className="mt-4 p-4 bg-slate-50 rounded-xl">
            <p className="text-sm font-medium text-slate-700 mb-2">Secili Mod: <span className="text-indigo-600">{activeMode.label}</span></p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-slate-500">Kiraci Online Odeme:</span>
              <span className="font-medium">{activeMode.tenantOnline ? "Acik" : "Kapali"}</span>
              <span className="text-slate-500">Manuel Yukleme:</span>
              <span className="font-medium">Her Zaman Acik</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* POS Settings - Only show if POS mode selected */}
      {settings?.payment_mode === "customer_pos" && (
        <Card className="border-emerald-200 shadow-sm" data-testid="pos-settings-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CreditCard size={20} className="text-emerald-600" />
              POS Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">POS saglayicinizin verdigi bilgileri girin. POS entegrasyonu aktif oldugunda kiracilariniz online odeme yapabilir.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Merchant ID</Label>
                <Input value={settings?.pos_merchant_id || ""} onChange={(e) => setSettings({ ...settings, pos_merchant_id: e.target.value })}
                  placeholder="POS Merchant ID" data-testid="pos-merchant-id" />
              </div>
              <div className="space-y-2">
                <Label>Terminal ID</Label>
                <Input value={settings?.pos_terminal_id || ""} onChange={(e) => setSettings({ ...settings, pos_terminal_id: e.target.value })}
                  placeholder="POS Terminal ID" data-testid="pos-terminal-id" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("settings.secret_key")}</Label>
              <Input type="password" value={settings?.pos_secret_key || ""} onChange={(e) => setSettings({ ...settings, pos_secret_key: e.target.value })}
                placeholder="POS Secret Key" data-testid="pos-secret-key" />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">POS entegrasyonu yakinда aktif olacaktir. Simdilik bilgilerinizi kaydedebilirsiniz.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {settings?.payment_mode === "platform_pos" && (
        <Card className="border-amber-200 shadow-sm" data-testid="platform-pos-info">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Bank size={20} className="text-amber-600" />
              Platform POS Bilgisi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">Platform POS modunda kiracilariniz Elworks POS sistemi uzerinden odeme yapar. Odeme tutari komisyon kesildikten sonra hesabiniza aktarilir.</p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">Platform POS entegrasyonu yakinda aktif olacaktir.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Banka IBAN - Her zaman gorunur (Havale/EFT icin gerekli) */}
      <Card className="border-blue-200 shadow-sm" data-testid="bank-iban-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Bank size={20} className="text-blue-600" />
            Banka Hesap Bilgisi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">Kiracilarinizin havale/EFT yapabilmesi icin IBAN bilginizi girin. Bu bilgi kiraci yukleme sayfasinda gosterilecektir.</p>
          <div className="space-y-2">
            <Label>Banka IBAN</Label>
            <Input value={settings?.bank_iban || ""} onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value })}
              placeholder="TR00 0000 0000 0000 0000 0000 00" data-testid="bank-iban" />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.account_name")}</Label>
            <Input value={settings?.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Firma veya hesap sahibi adi" data-testid="company-name-input" />
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Topup Limits */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Wallet size={20} className="text-indigo-600" />
            Yukleme Limitleri ve Hazir Tutarlar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Kiracilarin tek seferde yukleyebilecegi minimum/maksimum tutarlari ve hazir buton degerlerini belirleyin.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Minimum Yukleme Tutari (TL)</Label>
              <Input type="number" step="1" min="1" value={settings?.topup_min_amount || 10}
                onChange={(e) => setSettings({ ...settings, topup_min_amount: parseFloat(e.target.value) || 1 })}
                data-testid="topup-min-amount" />
            </div>
            <div className="space-y-2">
              <Label>Maksimum Yukleme Tutari (TL)</Label>
              <Input type="number" step="1" min="1" value={settings?.topup_max_amount || 1000}
                onChange={(e) => setSettings({ ...settings, topup_max_amount: parseFloat(e.target.value) || 1 })}
                data-testid="topup-max-amount" />
            </div>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Hazir Tutar Butonlari (TL)</Label>
            <p className="text-xs text-slate-400">Kiracinin hizlica secebilecegi hazir TL degerleri. En fazla 6 adet.</p>
            <div className="flex flex-wrap gap-2">
              {(settings?.topup_preset_amounts || [50, 100, 200, 500]).map((val, idx) => (
                <div key={idx} className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1">
                  <Input type="number" min="1" step="1"
                    className="w-20 h-7 text-xs border-0 bg-transparent p-0 text-center font-semibold"
                    value={val}
                    onChange={(e) => {
                      const newPresets = [...(settings?.topup_preset_amounts || [50, 100, 200, 500])];
                      newPresets[idx] = parseFloat(e.target.value) || 0;
                      setSettings({ ...settings, topup_preset_amounts: newPresets });
                    }}
                    data-testid={`preset-input-${idx}`} />
                  <span className="text-xs text-indigo-600 font-medium">TL</span>
                  <button type="button" className="text-red-400 hover:text-red-600 ml-1"
                    onClick={() => {
                      const newPresets = (settings?.topup_preset_amounts || [50, 100, 200, 500]).filter((_, i) => i !== idx);
                      setSettings({ ...settings, topup_preset_amounts: newPresets });
                    }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              {(settings?.topup_preset_amounts || [50, 100, 200, 500]).length < 6 && (
                <Button type="button" variant="outline" size="sm" className="h-9 text-xs"
                  onClick={() => {
                    const presets = [...(settings?.topup_preset_amounts || [50, 100, 200, 500])];
                    presets.push(0);
                    setSettings({ ...settings, topup_preset_amounts: presets });
                  }}
                  data-testid="add-preset-btn">
                  + Ekle
                </Button>
              )}
            </div>
          </div>
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-xs text-indigo-700">Bu limitler ve hazir tutarlar hem online odeme hem de manuel yukleme icin gecerlidir.</p>
          </div>
        </CardContent>
      </Card>

      {/* Low kWh Alert Threshold */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Bell size={20} className="text-amber-600" />
            Dusuk Bakiye Uyari Seviyesi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Kiracinin bakiyesi bu seviyenin altina dustugunde hem kiraciya hem size bildirim gonderilir.</p>
          <div className="max-w-xs space-y-2">
            <Label>Uyari Esigi (kWh)</Label>
            <Input type="number" step="1" min="0" value={settings?.low_kwh_threshold || 50}
              onChange={(e) => setSettings({ ...settings, low_kwh_threshold: parseFloat(e.target.value) || 0 })}
              data-testid="low-kwh-threshold" />
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700">Bakiye sifira dustugunde elektrik otomatik kesilir ve kritik uyari gonderilir.</p>
          </div>
        </CardContent>
      </Card>

      {/* Tenant Consumption Visibility */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Eye size={20} className="text-purple-600" />
            Kiraci Goruntuleme Yetkileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-testid="consumption-toggle-card">
            <div>
              <p className="text-sm font-medium text-slate-900">Tuketim Grafiklerini Kiraciya Goster</p>
              <p className="text-xs text-slate-500 mt-1">Kapali oldugunda kiracilar tuketim ve yukleme grafiklerini goremez</p>
            </div>
            <Switch
              checked={settings?.show_consumption_to_tenant ?? true}
              onCheckedChange={(checked) => setSettings({ ...settings, show_consumption_to_tenant: checked })}
              data-testid="show-consumption-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Custom Domain */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <GearSix size={20} className="text-blue-600" />
            Alan Adi (Domain)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">E-postalardaki onay linkleri ve bildirim URL'leri icin kullanilacak domain adresinizi belirleyin.</p>
          <div className="max-w-md space-y-2">
            <Label>Domain (orn: https://nanomet.net)</Label>
            <Input type="url" placeholder="https://yourdomain.com"
              value={settings?.custom_domain || ""}
              onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
              data-testid="custom-domain-input" />
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">Bu alanı bos biraktiginizda sistem varsayilan URL'yi kullanir. Kiracilara gonderilen e-postalardaki onay linkleri bu adresi kullanir.</p>
          </div>
        </CardContent>
      </Card>

      {/* Sabit Aylik Ucret */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <CurrencyCircleDollar size={20} className="text-amber-600" />
            Sabit Aylik Ucret
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-testid="fixed-fee-toggle-card">
            <div>
              <p className="text-sm font-medium text-slate-900">Sabit Ucret Aktif</p>
              <p className="text-xs text-slate-500 mt-1">Her ayin basinda kiraci bakiyesinden otomatik kesilir</p>
            </div>
            <Switch
              checked={settings?.fixed_fee_enabled ?? false}
              onCheckedChange={(checked) => setSettings({ ...settings, fixed_fee_enabled: checked })}
              data-testid="fixed-fee-toggle"
            />
          </div>
          {settings?.fixed_fee_enabled && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Sabit Ucret Tutari (TL)</Label>
                <Input type="number" min="0" step="1"
                  value={settings.fixed_fee_amount || ""}
                  onChange={(e) => setSettings({ ...settings, fixed_fee_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="orn: 50"
                  data-testid="fixed-fee-amount" />
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
                <p className="text-xs text-amber-700 font-medium">Sabit ucret akisi:</p>
                <p className="text-xs text-amber-600">1. Her ayin 1'inde kiracinin TL bakiyesinden kesilir</p>
                <p className="text-xs text-amber-600">2. Bakiye yetersizse kiraci borclandirilir</p>
                <p className="text-xs text-amber-600">3. 15. gune kadar borc odenmezse sayac kapatilir</p>
                <p className="text-xs text-amber-600">4. Borc odendiginde sayac otomatik acilir</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerSettingsPage;
